import { formatNumber } from "@angular/common";
import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnChanges, Output, SimpleChanges } from "@angular/core";
import { abbreviate } from "../../charts/bar-chart/bar-chart.component";
import { CURRENT_STATE_PROJECT_KEY, getInsights, Linqset } from "../../common/linqset-model";
import { DEFAULT_CURRENCY, getCurrency } from "../../common/organisation";
import { OrganisationService } from "../../organisation/organisation.service";
import { InsightsFieldType } from "../model/insights-field-type";
import { NODE_TYPE_DATA_TYPE, NodeTypeData } from "../node-types/node-type-data";
import { CompareRow, compareRows } from "./node-chart-data";

// each node before and after a change: current state against the project, as two dots joined by a line, the largest
// change first. Clearer than paired bars once there are more than a few nodes, and shows what a project moves
@Component({
    selector: 'app-node-compare-chart',
    templateUrl: './node-compare.html',
    styleUrls: ['./node-charts.css'],
    standalone: false
})
export class NodeCompareComponent implements OnChanges {
    @Input() linqset: Linqset
    @Input() comparison: Linqset
    @Input() typeData: NodeTypeData
    @Input() fieldType: InsightsFieldType
    @Input() trigger: any
    @Input() topNumber: number

    @Output() selectNodeEvent = new EventEmitter<string>();

    rows: CompareRow[] = []
    shown: CompareRow[] = []
    summary = { unchanged: 0, before: 0, after: 0, up: 0, down: 0, max: 0 }
    beforeName = ''
    afterName = ''
    ticks: number[] = []
    log = false
    colour = '#5b6172'

    constructor(@Inject(LOCALE_ID) private locale: string, private organisationService: OrganisationService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['trigger'] || changes['typeData'] || changes['fieldType'] || changes['topNumber'] || changes['comparison'] || changes['linqset']) this.populate();
    }

    get fieldName() {
        return String(this.fieldType?.type || '').toLowerCase();
    }

    // before is current state; when neither is, the one compared with
    private get sides() {
        const linqsetFirst = this.linqset.workspaceKey === CURRENT_STATE_PROJECT_KEY;
        return linqsetFirst ? { before: this.linqset, after: this.comparison, linqsetBefore: true } : { before: this.comparison, after: this.linqset, linqsetBefore: false };
    }

    private items() {
        const t = this.typeData, f = this.fieldType;
        const { linqsetBefore } = this.sides;
        const pair = (here: number, there: number) => linqsetBefore ? { before: here, after: there } : { before: there, after: here };
        if (t.dataType === NODE_TYPE_DATA_TYPE.MODELS) {
            return (t.models || []).map(m => ({
                key: m._key, name: m.name,
                ...pair(f.getSingleValueFromInsights(m.insights.find(i => i.projectKey === this.linqset.workspaceKey), t.dataType) || 0,
                    f.getSingleValueFromInsights(m.insights.find(i => i.projectKey === this.comparison.workspaceKey), t.dataType) || 0),
            }));
        }
        const list = (t.list || []).filter(n => !n.excluded).map((n, i) => ({
            key: n._key, name: n.name || 'Missing name ' + i,
            ...pair(f.getSingleValueFromInsights(getInsights(n, this.linqset), t.dataType) || 0, f.getSingleValueFromInsights(getInsights(n, this.comparison), t.dataType) || 0),
        }));
        // what is only in the comparison: nothing here
        const extra = (t.comparisonList || []).filter(n => !t.list.find(l => l._key === n._key)).map(n => ({
            key: n._key, name: n.name || 'Missing name', ...pair(0, f.getSingleComparisonValue(n, t.dataType) || 0),
        }));
        return [...list, ...extra];
    }

    private populate() {
        if (!this.typeData || !this.linqset || !this.comparison || !this.fieldType?.isValid()) return;
        const { before, after } = this.sides;
        this.beforeName = before.workspaceName || 'Before';
        this.afterName = after.workspaceName || 'After';
        const result = compareRows(this.items());
        this.rows = result.rows;
        this.summary = result;
        this.shown = this.rows.slice(0, this.topNumber || 35);
        const values = this.shown.flatMap(r => [r.before, r.after]).filter(v => v > 0).sort((a, b) => a - b);
        const top = this.niceMax(values[values.length - 1] || 0);
        // when the largest dwarfs the typical one, a log scale, so the rest aren't squeezed against 0
        const median = values[Math.floor(values.length / 2)] || 0;
        this.log = !!median && top / median > 20;
        this.ticks = !top ? [] : this.log
            ? [0, ...Array.from({ length: Math.floor(Math.log10(top)) + 1 }, (_, i) => Math.pow(10, i)).filter(t => t >= 10 && Math.log10(1 + t) / Math.log10(1 + top) < 0.88), top]
            : [0, top / 4, top / 2, 3 * top / 4, top];
        this.colour = this.typeData.colour || this.colour;
    }

    // a round number just above the largest, for the scale
    private niceMax(v: number) {
        if (!v) return 0;
        const step = Math.pow(10, Math.floor(Math.log10(v)));
        return Math.ceil(v / step * 2) / 2 * step;
    }

    get top() {
        return this.ticks[this.ticks.length - 1] || 1;
    }

    private position(v: number) {
        const p = this.log ? Math.log10(1 + Math.max(0, v)) / Math.log10(1 + this.top) : v / this.top;
        return Math.max(0, Math.min(100, 100 * p));
    }

    at(v: number) {
        return `${this.position(v)}%`;
    }

    width(r: CompareRow) {
        return `${Math.abs(this.position(r.after) - this.position(r.before))}%`;
    }

    left(r: CompareRow) {
        return this.at(Math.min(r.before, r.after));
    }

    value(v: number, short = true) {
        const symbol = this.fieldType?.isCurrency() ? getCurrency(this.organisationService.organisation?.currencyCode || DEFAULT_CURRENCY).symbol : '';
        const units = !symbol && this.fieldType?.getUnits() ? ' ' + this.fieldType.getUnits() : '';
        const text = short ? abbreviate(Math.abs(v), this.locale) : formatNumber(Math.round(Math.abs(v)), this.locale, '1.0-0') + units;
        return (v < 0 ? '-' : '') + symbol + text;
    }

    change(r: CompareRow) {
        const pct = r.before ? ` (${r.change > 0 ? '+' : ''}${Math.round(100 * r.change / r.before)}%)` : ' (new)';
        return `${r.change > 0 ? '▲ +' : '▼ '}${this.value(r.change)}${r.after === 0 ? ' (gone)' : pct}`;
    }

    tip(r: CompareRow) {
        return `${r.name}: ${this.value(r.before, false)} in ${this.beforeName}, ${this.value(r.after, false)} in ${this.afterName}`;
    }

    totalChange() {
        const { before, after } = this.summary;
        const d = after - before;
        return `${d >= 0 ? 'up' : 'down'} ${this.value(Math.abs(d), false)}${before ? ` (${Math.round(100 * Math.abs(d) / before)}%)` : ''}`;
    }

    select(r: CompareRow) {
        this.selectNodeEvent.emit(r.key);
    }
}
